<template>
    <div class="app-menu-dialog-container-background" :style="{ display: isOpen ? 'block' : 'none' }" @click="onBackgroundClick">
        <div class="app-menu-dialog-container" tabindex="-1" aria-modal="true" role="dialog" :aria-label="title" @click.stop :style="{ width: width }">
            <div class="app-menu-dialog-header">
                <div class="app-menu-dialog-title">
                    <span v-if="tag" class="app-menu-dialog-tag">{{ tag }}</span>
                    {{ title }}
                </div>
                <button 
                    v-if="showCloseButton" 
                    class="app-menu-dialog-close" 
                    @click.stop="onCloseClick"
                    aria-label="Close dialog"
                >
                    <i class="app-icon-close" aria-hidden="true"></i>
                </button>
            </div>
            <div class="app-menu-dialog-content">
                <slot></slot>
            </div>
        </div>
    </div>
</template>

<script>
    import adaMixin from '@/js/mixins/adaMixin';
    
    export default {
        name: 'menu-dialog',
        mixins: [adaMixin],
        props: {
            isOpen: Boolean,
            title: String,
            tag: String,
            showCloseButton: Boolean,
            width: String
        },
        data() {
            return {
                previouslyFocusedElement: null,
                focusEventListener: null,
            };
        },
        watch: {
            isOpen(newVal) {
                if (newVal) {
                    // Store the currently focused element
                    this.previouslyFocusedElement = document.activeElement;
                    
                    // Prevent body scroll when dialog opens
                    document.body.style.overflow = 'hidden';
                    // Add escape key listener
                    document.addEventListener('keydown', this.handleEscapeKey);
                    
                    // Setup focus trapping after dialog is rendered
                    this.$nextTick(() => {
                        const dialogContainer = this.$el.querySelector('.app-menu-dialog-container');
                        if (dialogContainer) {
                            // Focus the dialog container
                            dialogContainer.focus();
                            
                            // Set up focus trapping
                            this.trapModalFocus(dialogContainer);
                            
                            // Find first focusable element and focus it
                            const focusableElements = dialogContainer.querySelectorAll(
                                'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'
                            );
                            if (focusableElements.length > 0) {
                                focusableElements[0].focus();
                            }
                        }
                    });
                } else {
                    // Restore body scroll when dialog closes
                    document.body.style.overflow = '';
                    // Remove escape key listener
                    document.removeEventListener('keydown', this.handleEscapeKey);
                    
                    // Restore focus to previously focused element
                    if (this.previouslyFocusedElement && this.previouslyFocusedElement.focus) {
                        this.previouslyFocusedElement.focus();
                    }
                }
            }
        },
        methods: {
            onCloseClick() {
                this.$emit('close');
            },
            onBackgroundClick() {
                this.$emit('close');
            },
            handleEscapeKey(event) {
                if (event.key === 'Escape' || event.keyCode === 27) {
                    this.$emit('close');
                }
            }
        },
        beforeDestroy() {
            // Ensure body scroll is restored if component is destroyed
            document.body.style.overflow = '';
            // Remove escape key listener
            document.removeEventListener('keydown', this.handleEscapeKey);
            // Restore focus if dialog was open when destroyed
            if (this.previouslyFocusedElement && this.previouslyFocusedElement.focus) {
                this.previouslyFocusedElement.focus();
            }
        }
    }
</script>
<style scoped>
    .app-menu-dialog-container-background {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background-color: rgba(0, 0, 0, 0.25);
        z-index: 1000;
        overflow: hidden;
        overscroll-behavior: contain;
    }

    .app-menu-dialog-container {
        position: fixed;
        background-color: #fff;
        left: 50%;
        top: 50%;
        width: 90%;
        max-width: 600px;
        max-height: 90vh;
        transform: translate(-50%, -50%);
        border-radius: 16px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    }

    .app-menu-dialog-header {
        padding: 24px 24px 12px 24px;
        flex-shrink: 0;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;
    }

    .app-menu-dialog-content {
        padding: 12px 24px 24px 24px;
        overflow-y: auto;
        flex: 1;
    }

    .app-menu-dialog-title {
        font-size: 18px;
        font-weight: 600;
        line-height: 125%;
        color: #272932;
        margin-left: 0;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .app-menu-dialog-tag {
        padding: 8px 14px;
        background-color: #C2E66E;
        border-radius: 10px;
        font-size: 14px;
        font-weight: 600;
        white-space: nowrap;
    }

    .app-menu-dialog-close {
        background-color: transparent;
        border: none;
        cursor: pointer;
        height: 24px;
        width: 24px;
        padding: 0px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    @media (max-width: 767px) {
        .app-menu-dialog-container {
            width: 95%;
            max-width: 95%;
            max-height: 85vh;
            padding: 16px;
        }
    }
</style>